import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import SCENES from '@/components/ambience/scenes';
import '@/styles/ambience.css';

/**
 * The animated nature layer behind the app.
 *
 * Owner, 6 Jun 2026: "there should be background scenery where these things
 * happen — different scenery for different animated themes, natural elements
 * in the background, and the motion should be slow so it doesn't create a
 * headache." So every live theme renders three stacked pieces:
 *   sky  — the light of that hour
 *   scene — the PLACE (mountains, forest, sea, cliffs, canopy) as flat SVG
 *   fx   — the weather happening in it, all of it deliberately slow
 *
 * Renders nothing at all unless the active theme declares an `ambience`, so
 * every other theme costs zero DOM and zero animation. Sits fixed BEHIND the
 * content with `pointer-events: none`; cards are opaque, so the scene reads
 * like a wallpaper and can never sit under text.
 */

// A leaf needs its own node to tumble on its own axis. Durations are 30-56s: a
// leaf takes most of a minute to cross the screen, which is what "slow" means
// here. Values are hand-picked rather than random so the scene is the same
// every load — a layer that reshuffles on each render is a distraction.
// 6 Jun 2026: was 14 small pale-green leaves and the owner could not see them.
// Now 22, bigger, and in autumn colours instead of one flat green — a falling
// leaf that is the same colour as the wood behind it is invisible.
// Delays are NEGATIVE on purpose: a negative delay starts the animation
// partway through, so all 22 leaves are already in flight on the first frame.
// With positive delays the screen starts almost empty and takes a minute to
// fill, which reads as "the leaves are broken".
const LEAF_COLOURS = [
  'rgba(203, 133, 54, 0.88)',  // amber
  'rgba(220, 168, 74, 0.85)',  // gold
  'rgba(168, 180, 87, 0.85)',  // olive
  'rgba(180, 85, 47, 0.88)',   // rust
  'rgba(228, 190, 102, 0.8)',  // straw
];
const LEAVES = [
  { l: '3%', s: 18, d: 38, delay: -0, x: '8vw', spin: '300deg', o: 0.85, c: 0 },
  { l: '9%', s: 13, d: 48, delay: -7, x: '-5vw', spin: '-340deg', o: 0.7, c: 2 },
  { l: '15%', s: 20, d: 32, delay: -15, x: '11vw', spin: '240deg', o: 0.9, c: 1 },
  { l: '21%', s: 12, d: 52, delay: -3, x: '6vw', spin: '400deg', o: 0.62, c: 3 },
  { l: '27%', s: 16, d: 42, delay: -22, x: '-9vw', spin: '-300deg', o: 0.8, c: 4 },
  { l: '33%', s: 14, d: 46, delay: -11, x: '9vw', spin: '320deg', o: 0.72, c: 0 },
  { l: '39%', s: 19, d: 34, delay: -28, x: '-7vw', spin: '-260deg', o: 0.88, c: 3 },
  { l: '45%', s: 11, d: 56, delay: -5, x: '5vw', spin: '440deg', o: 0.58, c: 1 },
  { l: '51%', s: 15, d: 44, delay: -18, x: '-11vw', spin: '300deg', o: 0.75, c: 2 },
  { l: '57%', s: 21, d: 30, delay: -9, x: '8vw', spin: '-280deg', o: 0.9, c: 0 },
  { l: '63%', s: 13, d: 50, delay: -25, x: '-4vw', spin: '360deg', o: 0.66, c: 4 },
  { l: '69%', s: 17, d: 40, delay: -13, x: '7vw', spin: '260deg', o: 0.82, c: 1 },
  { l: '75%', s: 12, d: 54, delay: -31, x: '12vw', spin: '480deg', o: 0.6, c: 2 },
  { l: '81%', s: 19, d: 36, delay: -2, x: '-8vw', spin: '-500deg', o: 0.86, c: 3 },
  { l: '87%', s: 14, d: 47, delay: -20, x: '6vw', spin: '280deg', o: 0.74, c: 0 },
  { l: '93%', s: 16, d: 43, delay: -34, x: '-6vw', spin: '-320deg', o: 0.78, c: 4 },
  { l: '6%', s: 11, d: 51, delay: -26, x: '10vw', spin: '420deg', o: 0.56, c: 1 },
  { l: '30%', s: 22, d: 33, delay: -7, x: '-10vw', spin: '-240deg', o: 0.92, c: 3 },
  { l: '48%', s: 12, d: 53, delay: -36, x: '9vw', spin: '380deg', o: 0.6, c: 2 },
  { l: '66%', s: 18, d: 39, delay: -5, x: '-5vw', spin: '-300deg', o: 0.84, c: 0 },
  { l: '78%', s: 13, d: 49, delay: -6, x: '7vw', spin: '340deg', o: 0.68, c: 4 },
  { l: '96%', s: 20, d: 35, delay: -16, x: '-9vw', spin: '-260deg', o: 0.88, c: 1 },
];

// How deep the snow lies, 0-1, from the clock: a thin cover first thing in the
// morning, deep by the evening. Read by `.amb--snow .s-drift` in ambience.css.
const snowDepth = () => {
  const d = new Date();
  return (d.getHours() * 60 + d.getMinutes()) / 1440;
};

const Sheets = ({ n }) => Array.from({ length: n }, (_, i) => (
  <div key={i} className={`amb__l amb__l--${i + 1}`} />
));

const Bands = ({ n }) => Array.from({ length: n }, (_, i) => (
  <div key={i} className={`amb__band amb__band--${i + 1}`} />
));

// Birds live out here rather than in the scene SVG so a flock can cross the
// whole viewport; the scene SVG is pinned to its own viewBox.
const Birds = ({ n }) => (
  <div className={`amb__birds amb__birds--${n}`}>
    <svg viewBox="0 0 300 120" aria-hidden="true">
      <path className="amb__bird amb__bird--a" d="M12 44 Q28 26 44 42 Q60 26 76 44" />
      <path className="amb__bird amb__bird--b" d="M128 78 Q140 64 152 76 Q164 62 176 78" />
      <path className="amb__bird amb__bird--c" d="M216 24 Q226 12 236 22 Q246 10 256 24" />
    </svg>
  </div>
);

const Weather = ({ kind }) => {
  if (kind === 'rain') return <><Sheets n={3} /><Bands n={2} /></>;
  if (kind === 'fog') {
    // Ribbons, not blobs: see the note in ambience.css. Written out rather
    // than mapped so each one keeps its own speed and height.
    return (
      <>
        <div className="amb__mistband amb__mistband--1" />
        <div className="amb__mistband amb__mistband--2" />
        <div className="amb__mistband amb__mistband--3" />
        <div className="amb__mistband amb__mistband--4" />
      </>
    );
  }
  if (kind === 'sunrise' || kind === 'sunset') {
    return (
      <>
        <div className="amb__glow" />
        <Bands n={2} />
        <Birds n={1} />
        <Birds n={2} />
      </>
    );
  }
  if (kind === 'snow') {
    // Two swaying wrappers so the near and far flakes drift out of step.
    return (
      <>
        <div className="amb__sway amb__sway--a">
          <div className="amb__l amb__l--1" />
          <div className="amb__l amb__l--3" />
        </div>
        <div className="amb__sway amb__sway--b">
          <div className="amb__l amb__l--2" />
        </div>
      </>
    );
  }
  if (kind === 'waterfall') {
    return (
      <>
        <div className="amb__fall"><Sheets n={2} /></div>
        <div className="amb__mist" />
        <Birds n={1} />
        <Birds n={2} />
      </>
    );
  }
  if (kind === 'leaves') {
    return LEAVES.map((f, i) => (
      <span key={i} className="amb__leaf" style={{
        '--leaf-l': f.l, '--leaf-s': `${f.s}px`, '--leaf-d': `${f.d}s`,
        '--leaf-delay': `${f.delay}s`, '--leaf-x': f.x, '--leaf-spin': f.spin,
        '--leaf-o': f.o, '--leaf-c': LEAF_COLOURS[f.c],
      }} />
    ));
  }
  return null;
};

export const ThemeAmbience = () => {
  const { themes, themeName, motion } = useTheme();
  const kind = themes[themeName]?.ambience;
  // A `-day` / `-night` variant is the SAME drawing and the same weather as
  // the scene it is based on — only the palette differs, so it carries both
  // classes: the base one for the geometry and animation, the full one for the
  // colours. (Four scenes were born dark and gained `-day`; three were born
  // light and gained `-night`. Same mechanism either way.)
  const base = kind ? kind.replace(/-(day|night)$/, '') : null;
  const Scene = base ? SCENES[base] : null;

  const scene = useMemo(() => (Scene ? <Scene /> : null), [Scene]);

  // Re-read the clock every ten minutes so the snow keeps building while the
  // tab is open, without re-rendering anything else.
  const [depth, setDepth] = useState(snowDepth);
  useEffect(() => {
    const id = setInterval(() => setDepth(snowDepth()), 600000);
    return () => clearInterval(id);
  }, []);

  if (!kind) return null;
  return (
    <div
      className={`amb amb--${base}${kind === base ? '' : ` amb--${kind}`}${motion ? '' : ' amb--still'}`}
      style={base === 'snow' ? { '--snow-depth': depth } : undefined}
      aria-hidden="true"
      data-testid={`theme-ambience-${kind}`}
      data-motion={motion ? 'on' : 'off'}
    >
      <div className="amb__sky" />
      {scene}
      <div className="amb__fx"><Weather kind={base} /></div>
    </div>
  );
};

export default ThemeAmbience;
